import { useEffect, useMemo, useState } from "react";
import {
  createStory,
  getStory,
  listStories,
  patchStory,
  publishStory
} from "../api/endpoints";
import type { Story } from "../api/types";
import { useI18n } from "../i18n";

type Tab = "public" | "mine" | "partner";

export default function StoriesPage() {
  const { t } = useI18n();

  const [tab, setTab] = useState<Tab>("public");
  const [items, setItems] = useState<Story[]>([]);
  const [active, setActive] = useState<Story | null>(null);

  const [draftTitle, setDraftTitle] = useState("");
  const [draftContent, setDraftContent] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tabLabel = useMemo(() => {
    return (k: Tab) => t(`stories.tabs.${k}`);
  }, [t]);

  const statusLabel = useMemo(() => {
    return (status: string) => {
      const key = `stories.status.${String(status || "").toUpperCase()}`;
      const v = t(key);
      return v === key ? status : v;
    };
  }, [t]);

  const visibilityLabel = useMemo(() => {
    return (vis: string) => {
      const key = `stories.visibility.${String(vis || "").toUpperCase()}`;
      const v = t(key);
      return v === key ? vis : v;
    };
  }, [t]);

  async function load(scope: Tab) {
    setLoading(true);
    setError(null);
    try {
      const res = await listStories(scope);
      setItems(res.items || []);
    } catch (e: any) {
      setError(e?.message || t("stories.errors.loadFailed"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(tab);
  }, [tab]);

  async function openStory(id: string) {
    try {
      const s = await getStory(id);
      setActive(s);
    } catch (e: any) {
      alert(e?.message || t("stories.errors.openFailed"));
    }
  }

  async function handleCreate() {
    if (!draftTitle.trim() || !draftContent.trim()) return;
    try {
      const story = await createStory({
        title: draftTitle.trim(),
        content: draftContent.trim(),
        visibility: "PRIVATE"
      });
      setDraftTitle("");
      setDraftContent("");
      setItems((prev) => [story, ...prev]);
      setActive(story);
    } catch (e: any) {
      alert(e?.message || t("stories.errors.createFailed"));
    }
  }

  async function handlePublish(id: string) {
    try {
      const story = await publishStory(id);
      setActive(story);
      setItems((prev) => prev.map((s) => (s.id === id ? story : s)));
    } catch (e: any) {
      alert(e?.message || t("stories.errors.publishFailed"));
    }
  }

  async function handleSaveEdit() {
    if (!active) return;
    try {
      const updated = await patchStory(active.id, {
        title: active.title,
        content: active.content
      });
      setActive(updated);
      setItems((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (e: any) {
      alert(e?.message || t("stories.errors.saveFailed"));
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{t("stories.title")}</div>
          <div className="page-subtitle">{t("stories.subtitle")}</div>
        </div>
        <div className="chip-row">
          {(["public", "mine", "partner"] as Tab[]).map((k) => (
            <button
              key={k}
              className={tab === k ? "btn-primary" : "btn-ghost"}
              onClick={() => setTab(k)}
            >
              {tabLabel(k)}
            </button>
          ))}
        </div>
      </div>

      <div className="cards-grid">
        <div className="card">
          <div className="section-title">{t("stories.newTitle")}</div>
          <div className="section-description">{t("stories.newDesc")}</div>

          <div className="form-field">
            <label className="form-label">{t("stories.fields.title")}</label>
            <input
              className="form-input"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
            />
          </div>

          <div className="form-field">
            <label className="form-label">{t("stories.fields.content")}</label>
            <textarea
              className="form-textarea"
              rows={4}
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
            />
          </div>

          <button className="btn-primary" onClick={handleCreate}>
            {t("stories.saveDraft")}
          </button>
        </div>

        <div className="card">
          <div className="section-title">
            {t("stories.listTitle")} ({loading ? t("common.loading") : items.length})
          </div>

          {error && <p className="form-error">{error}</p>}

          <div
            style={{
              maxHeight: 280,
              overflowY: "auto",
              paddingRight: "0.5rem"
            }}
          >
            {items.map((s) => (
              <div
                key={s.id}
                style={{
                  padding: "0.35rem 0",
                  borderBottom: "1px solid rgba(31,41,55,0.6)",
                  cursor: "pointer"
                }}
                onClick={() => void openStory(s.id)}
              >
                <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>{s.title}</div>
                <div className="text-muted small">
                  {visibilityLabel(String(s.visibility))} · {statusLabel(String(s.status))}
                </div>
              </div>
            ))}

            {!items.length && !loading && !error && (
              <p className="text-muted small">{t("stories.empty")}</p>
            )}
          </div>
        </div>

        {active && (
          <div className="card">
            <div className="section-title">{t("stories.editTitle")}</div>

            <div className="form-field">
              <label className="form-label">{t("stories.fields.title")}</label>
              <input
                className="form-input"
                value={active.title}
                onChange={(e) => setActive({ ...active, title: e.target.value })}
              />
            </div>

            <div className="form-field">
              <label className="form-label">{t("stories.fields.content")}</label>
              <textarea
                className="form-textarea"
                rows={6}
                value={active.content}
                onChange={(e) => setActive({ ...active, content: e.target.value })}
              />
            </div>

            <div className="chip-row">
              <button className="btn-outline" onClick={handleSaveEdit}>
                {t("stories.saveChanges")}
              </button>

              {String(active.status).toUpperCase() !== "APPROVED" && (
                <button className="btn-primary" onClick={() => void handlePublish(active.id)}>
                  {t("stories.submitForReview")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
